/**
 * Generates a random string of specified length.
 *
 * @param {number} length - The length of the generated string.
 * @returns {string} - The randomly generated string.
 */
export function generateRandomString (length) {
  let text = ''
  const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
  for (let i = 0; i < length; i++) {
    text += possible.charAt(Math.floor(Math.random() * possible.length))
  }
  return text
}

/**
 * Generates a code challenge for the given code verifier.
 * @param {string} codeVerifier - The code verifier to generate the challenge for.
 * @returns {Promise<string>} The generated code challenge.
 */
export async function generateCodeChallenge (codeVerifier) {
  const encoder = new TextEncoder()
  const data = encoder.encode(codeVerifier)
  const hashBuffer = await crypto.subtle.digest('SHA-256', data)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  const hashBase64 = btoa(String.fromCharCode(...hashArray))
  const base64URL = hashBase64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
  return base64URL
}

/**
 * Generates the code verifier, a random string of the specified length.
 *
 * @param {number} length - The length of the random string to generate.
 * @returns {string} The randomly generated string.
 */
export const codeVerifier = generateRandomString(128)

/**
 * Gets the appropriate base URL for redirects.
 * Uses APPLICATION_URL in production or window.location.origin for development.
 *
 * @returns {string} The base URL to use for redirects.
 */
export const getBaseUrl = () => {
  const isDevelopment = window.location.hostname === 'localhost'
  return isDevelopment ? window.location.origin : (process.env.APPLICATION_URL || window.location.origin)
}

export const getAuth = async (params) => {
  try {
    const baseUrl = getBaseUrl()
    const redirectUri = params.isLoginMos
      ? `${baseUrl}/login-mos`
      : `${baseUrl}${process.env.PATH_LOGIN}`

    const clientId = params.isLoginMos
      ? process.env.CLIENT_ID_MOS || process.env.CLIENT_ID
      : process.env.CLIENT_ID

    const urlLocation = new URL(window.location.href)
    const ott = urlLocation.searchParams.get('ott')
    window.sessionStorage.setItem('ott', ott)

    // iam authorize
    const url = `${process.env.API_REST_BE}/oauth2/authorize?redirect_uri=${redirectUri}&scope=${process.env.SCOPE}&code_challenge=${params.codeChallenge}&code_challenge_method=${process.env.CODE_CHALLENGE_METHOD}&client_id=${clientId}&response_type=${process.env.RESPONSE_TYPE}&nonce=${params.nonce}`

    window.localStorage.setItem('codeVerifier', params.codeVerifier)
    params.isLoginMos ? window.sessionStorage.setItem('isAuthorizedMos', true) : window.sessionStorage.setItem('isAuthorized', true)

    setTimeout(() => {
      window.location.replace(url)
    }, 2500)
  } catch (error) {
    console.error(error)
  }
}

export const getRegistrationAuth = (params) => {
  const baseUrl = getBaseUrl()
  const redirectUri = `${baseUrl}${process.env.PATH_LOGIN}`

  const url = `${process.env.API_REST_BE}/oauth2/authorize?redirect_uri=${redirectUri}&scope=${process.env.SCOPE}&code_challenge=${params.codeChallenge}&code_challenge_method=${process.env.CODE_CHALLENGE_METHOD}&client_id=${process.env.CLIENT_ID}&response_type=${process.env.RESPONSE_TYPE}&nonce=${params.nonce}`

  window.localStorage.setItem('codeVerifier', params.codeVerifier)
  window.sessionStorage.setItem('isAuthorized', true)

  setTimeout(() => {
    window.location.replace(url)
  }, 2500)
}

export const getAuthProcessAsync = async () => {
  const newCodeVerifier = generateRandomString(128)
  const codeChallenge = await generateCodeChallenge(newCodeVerifier)
  return {
    codeChallenge,
    nonce: generateRandomString(16),
    codeVerifier: newCodeVerifier,
    state: generateRandomString(32)
  }
}
